import LabelBackground from '@/components/LabelBackground/LabelBackground';
import IconButton from '@/components/IconButton/IconButton';
import ArrowLeftIcon from '@/components/Icon/ArrowLeftIcon';
import { measure } from '@/components/Analytics/Analytics';
import useSpecies from '@/hooks/species/useSpecies';
import {
	useBeforeUnload,
	useBlocker,
	useLocation,
	useNavigate,
} from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSeason from '@/hooks/useSeason';
import RecorderIcon from '@/components/Icon/RecorderIcon';
import { motion } from 'framer-motion';
import Recorder from 'opus-recorder';
import encoderPath from 'opus-recorder/dist/waveWorker.min.js?url';
import StopIcon from '@/components/Icon/StopIcon';
import Button from '@/components/Button/Button';
import ArrowRightIcon from '@/components/Icon/ArrowRightIcon';
import CloseIcon from '@/components/Icon/CloseIcon';
import useCards from '@/hooks/cards/useCards';
import { fileToBase64DataUrl, getMimeTypeFromDataUrl } from '@/lib/file';
import TopOverlayPattern from '@/components/TopOverlayPattern/TopOverlayPattern';
import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages, {
	useNativeMessageCallback,
} from '@/hooks/native/useNativeMessages';
import useTemporaryKeyValueStorage from '@/hooks/storage/useTemporaryKeyValueStorage';
import useHaptics from '@/hooks/useHaptics';

const MAX_RECORDING_TIME = 15;

const RecorderRoute = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const isNative = useIsNative();

	const { findSpeciesById, getSpeciesRouteById } = useSpecies();
	const { identifyAndStore } = useCards();
	const season = useSeason();

	// Get from which species the recorder was opened, using the state given by the navigate function
	const cameraOpenedFromSpeciesId = useLocation().state?.speciesId;
	const [targetSpeciesId, setTargetSpeciesId] = useState<number | null>(
		cameraOpenedFromSpeciesId
	);
	if (targetSpeciesId === undefined)
		console.error('cameraOpenedFromSpeciesId is undefined');
	// Check if cameraOpenedFromSpeciesId updates
	useEffect(() => {
		if (cameraOpenedFromSpeciesId)
			setTargetSpeciesId(cameraOpenedFromSpeciesId);
	}, [cameraOpenedFromSpeciesId]);

	const species = useMemo(
		() => findSpeciesById(targetSpeciesId ?? undefined),
		[findSpeciesById, targetSpeciesId]
	);

	// Create the recorder
	const [recordingCountDownValue, setRecordingCountDownValue] =
		useState(MAX_RECORDING_TIME);
	const [isRecording, setIsRecording] = useState(false);
	const [isRecorderError, setIsRecorderError] = useState<null | {
		message: string;
	}>(null);
	const { value: fullAudioBase64, setValue: setFullAudioBase64 } =
		useTemporaryKeyValueStorage<string | null>(
			'recorderFullAudioBase64',
			null
		);
	const [identifyingState, setIdentifyingState] = useState<
		null | 'identifying' | 'error' | 'success'
	>(null);
	const webRecorder = useRef(new Recorder({ encoderPath }));

	const onNativeMessage = useNativeMessageCallback((key, value) => {
		switch (key) {
			case 'recorder.start':
				setIsRecording(true);
				break;
			case 'recorder.error':
				setIsRecording(false);
				setIsRecorderError({
					message: value ?? 'app fout',
				});
				break;
			case 'recorder.recordingTaken':
				if (value) {
					setFullAudioBase64(value);
					// Reload, because on Android the recorder will mess with the audio players...
					// ...and audio players will be blocked until the page is reloaded
					// Reload is enabled on web too so there will be a lower chance of bugs appearing
					setTimeout(() => window.location.reload(), 500);
				}
				break;
			default:
				break;
		}
	}, []);
	const { sendNativeMessage } = useNativeMessages(onNativeMessage, [
		'recorder.start',
		'recorder.error',
		'recorder.recordingTaken',
	]);

	// Update the fullAudioBase64 when the webRecorder has data
	useEffect(() => {
		webRecorder.current.ondataavailable = async (
			arrayBuffer: ArrayBuffer
		) => {
			// Generate full audio blob from array buffers
			const fullAudioBlob = new Blob([arrayBuffer], {
				type: 'audio/wav',
			});
			setFullAudioBase64(await fileToBase64DataUrl(fullAudioBlob));

			// Reload, because on Android the recorder will mess with the audio players...
			// ...and audio players will be blocked until the page is reloaded
			// Reload is enabled on web too so there will be a lower chance of bugs appearing
			setTimeout(() => window.location.reload(), 500);
		};
	}, [setFullAudioBase64]);

	// Toggle recording
	const toggleRecording = useCallback(() => {
		setRecordingCountDownValue(MAX_RECORDING_TIME);
		if (!isRecording) {
			if (isNative) {
				sendNativeMessage('recorder.start');
			} else
				webRecorder.current
					.start()
					.then(() => {
						setIsRecording(true);
					})
					.catch((error: DOMException) => {
						setIsRecording(false);
						setIsRecorderError(error);
					});
		} else {
			if (isNative) {
				sendNativeMessage('recorder.stop');
			} else webRecorder.current.stop();
			setIsRecording(false);
		}
	}, [isRecording, isNative, sendNativeMessage]);

	// Countdown while recording and stop recording when time is up
	useEffect(() => {
		if (!isRecording) return;

		// Stop recording when time is up
		if (recordingCountDownValue < 1) {
			toggleRecording();
		}

		// Count down every second
		const intervalId = setInterval(() => {
			if (isRecording)
				setRecordingCountDownValue((value) => Math.max(value - 1, 0));
		}, 1000);

		// Clear interval when component unmounts
		return () => clearInterval(intervalId);
	}, [isRecording, recordingCountDownValue, toggleRecording]);

	// Reset recording to retry
	const resetRecording = useCallback(() => {
		setFullAudioBase64(null);
		setIsRecording(false);
		setIsRecorderError(null);
		setRecordingCountDownValue(MAX_RECORDING_TIME);
	}, [setFullAudioBase64]);

	// Send the recording to be identified
	const identifyRecording = useCallback(() => {
		if (!fullAudioBase64 || !species?.id) return;
		setIdentifyingState('identifying');
		identifyAndStore(fullAudioBase64, 'audio', species.id).then(
			(result) => {
				if (!targetSpeciesId) return;

				setIdentifyingState(
					result?.light === 'RED' ? 'error' : 'success'
				);

				if (result?.is_stored) {
					measure(
						`recorder-identified-success-${
							result.algorithm_step
						}-${targetSpeciesId}-${
							findSpeciesById(targetSpeciesId)?.attributes.slug
						}`
					);

					navigate(getSpeciesRouteById(result.species_id ?? 0), {
						replace: true,
					});
				} else if (result?.identified_as) {
					measure(
						`recorder-identified-error-${targetSpeciesId}-${
							findSpeciesById(targetSpeciesId)?.attributes.slug
						}`
					);

					// Notify the user of error
					navigate('/oeps', {
						state: {
							audio: fullAudioBase64,
							targetSpeciesId: targetSpeciesId,
							resultIdentifiedAsId: result.species_id,
							resultIdentifiedAsDisplayName: result.identified_as,
						},
					});
				} else {
					measure('recorder-identify-error-connection');

					// Notify the user of error (probably connection error)
					navigate('/oeps', {
						state: {
							audio: fullAudioBase64,
							targetSpeciesId: targetSpeciesId,
						},
					});
				}
				setFullAudioBase64(null);
			}
		);
	}, [
		findSpeciesById,
		fullAudioBase64,
		getSpeciesRouteById,
		identifyAndStore,
		navigate,
		setFullAudioBase64,
		species,
		targetSpeciesId,
	]);

	// If location changes, stop recorder
	const stopRecorder = useCallback(() => {
		if (isNative) sendNativeMessage('recorder.stop');
		else webRecorder.current.close();
	}, [isNative, sendNativeMessage]);
	useBeforeUnload(stopRecorder);
	useEffect(() => {
		if (location.pathname !== '/recorder') stopRecorder();
	}, [location, stopRecorder]);
	useBlocker(({ historyAction }) => {
		if (isRecording) toggleRecording();
		stopRecorder();
		if (fullAudioBase64 && historyAction !== 'PUSH') resetRecording();
		return isRecording || (!!fullAudioBase64 && historyAction !== 'PUSH');
	});

	return (
		<main className="relative flex h-dvh w-dvw flex-col overflow-y-auto overflow-x-clip bg-gray-950 text-white">
			<img
				alt=""
				src={`/assets/illustrations/bottom_end_${season}.svg`}
				className="absolute bottom-0 end-0 max-w-xs opacity-30"
			/>
			<nav className="flex flex-col items-center justify-center pt-4">
				<TopOverlayPattern className="absolute inset-0 h-24 w-full -rotate-6 scale-[6] text-accent opacity-20" />
				<IconButton
					className="absolute start-2 top-2 z-30 lg:start-4 lg:top-4"
					onClick={() => {
						if (targetSpeciesId && species)
							measure(
								`camera-back-${targetSpeciesId}-${species.attributes.slug}`,
								'exit'
							);
						navigate(-1);
					}}
					aria-label="Terug"
					title="Terug"
					icon={<ArrowLeftIcon />}
				/>
				<h1 className="relative px-4 pb-1 pt-2 font-brand text-xl xxs:text-3xl">
					<LabelBackground className="text-gray-950" />
					<span className="relative z-30">
						{species?.attributes.titleShort}
					</span>
				</h1>
			</nav>
			<div className="relative flex grow flex-col items-center justify-center gap-6 text-lg text-white">
				{!fullAudioBase64 && (
					<>
						{!isRecorderError ? (
							<>
								{isRecording && (
									<motion.p
										className="ps-3 font-brand text-8xl"
										initial={{
											height: 0,
										}}
										animate={{
											height: 'auto',
										}}
										aria-label={`nog ${recordingCountDownValue} seconden`}
										role="timer"
									>
										{recordingCountDownValue}
										<span className="text-3xl">s</span>
									</motion.p>
								)}
								<RecordButton
									isRecording={isRecording}
									toggleRecording={toggleRecording}
								/>
							</>
						) : (
							<p className="text-balance bg-slate-950 p-4 text-center">
								De microfoon kan niet worden gebruikt in
								SoortSafari. Het kan zijn dat er een instelling
								niet goed staat in je systeem. Als je graag wilt
								opnemen, vraag dan iemand om hulp.
								<br />
								Foutmelding: {isRecorderError.message}
							</p>
						)}
					</>
				)}
				{!!fullAudioBase64 && (
					<>
						<p className="sr-only" role="alert">
							Opname is beschikbaar
						</p>
						<AudioPlayer
							src={fullAudioBase64}
							mimeType={getMimeTypeFromDataUrl(fullAudioBase64)}
						/>
						{identifyingState === null && (
							<>
								<Button
									label="Check"
									backgroundClassName="text-accent"
									textClassName="text-accentCompat text-5xl py-1 ps-3"
									onClick={identifyRecording}
									icon={<ArrowRightIcon />}
								/>
								<Button
									label="Verwijder"
									backgroundClassName="text-primary"
									textClassName="text-white"
									onClick={resetRecording}
									icon={<CloseIcon />}
								/>
							</>
						)}
						{identifyingState === 'identifying' && (
							<p role="alert" className="text-center">
								Aan het checken...
							</p>
						)}
						{identifyingState === 'error' && (
							<p role="alert" className="text-center">
								Een {species?.attributes.titleLong} kon helaas
								niet worden gevonden in deze opname.
							</p>
						)}
					</>
				)}
			</div>
		</main>
	);
};

const RecordButton = ({
	isRecording,
	toggleRecording,
}: {
	isRecording: boolean;
	toggleRecording: () => void;
}) => {
	const { withOnButtonPress } = useHaptics();

	return (
		<motion.div
			className="rounded-full bg-accentCompat p-2"
			animate={{
				opacity: isRecording ? [1, 0.8, 1] : 1,
				transition: {
					duration: isRecording ? 1.5 : 0.2,
					repeat: isRecording ? Infinity : 0,
				},
			}}
		>
			<div className="rounded-full bg-black p-2">
				<motion.button
					className="rounded-full bg-accent p-4 focus-visible:scale-150"
					initial={{
						scale: 0,
					}}
					animate={{
						scale: isRecording ? [1, 1.1, 1] : 1,
						transition: {
							duration: isRecording ? 1.5 : 0.2,
							repeat: isRecording ? Infinity : 0,
						},
					}}
					whileTap={{
						scale: 0.95,
					}}
					onClick={toggleRecording}
					{...withOnButtonPress}
				>
					{isRecording ? (
						<StopIcon className="size-24 text-accentCompat lg:size-24" />
					) : (
						<RecorderIcon className="size-24 text-accentCompat lg:size-24" />
					)}
				</motion.button>
			</div>
		</motion.div>
	);
};

export default RecorderRoute;
